import { H3, Grid } from '@daren/ui-components'
import { PlusIcon } from '@heroicons/react/solid'
import * as React from 'react'

import { Button } from '~/components/buttons'
import { ArticleCard } from '~/components/elements'
import { Section, Header } from '~/components/layout'
import { useTranslations } from '~/context/translations-provider'
import type {
  SanityBlock,
  SanityPage,
  SanityPost,
  SanityTag,
} from '~/types/sanity'
import { filterPosts } from '~/utils/blog'
import { localizeSlug } from '~/utils/i18n'

// should be divisible by 3 and 2 (large screen, and medium screen).
const PAGE_SIZE = 6

interface ContentProps {
  title?: string
  subTitle?: string
  posts?: SanityPost[]
  featuredEnabled?: boolean
  count?: number
  tags?: SanityTag[]
  link?: SanityPage
}

export function PostsBlock({
  content: {
    count,
    link,
    posts: allPosts = [],
    subTitle,
    tags = [],
    title,
  } = {},
}: SanityBlock<ContentProps>) {
  const { t, currentLanguage } = useTranslations()

  const [indexToShow, setIndexToShow] = React.useState(PAGE_SIZE)
  let filteredPosts =
    tags.length > 0
      ? filterPosts(allPosts, tags.map(({ value }) => value).join(' '))
      : allPosts

  if (count) {
    filteredPosts = filteredPosts.slice(0, count)
  }

  React.useEffect(() => {
    setIndexToShow(PAGE_SIZE)
  }, [])

  const posts: SanityPost[] = filteredPosts.slice(0, indexToShow)

  const hasMorePosts = indexToShow < filteredPosts.length

  return (
    <>
      {(title || subTitle) && (
        <Section className="mt-24">
          <Header
            title={title}
            subTitle={subTitle}
            cta={t('see-all-articles')}
            ctaUrl={
              link?.slug && localizeSlug(link?.slug as string, currentLanguage)
            }
          />
        </Section>
      )}

      <Section className="mb-24">
        <Grid className="grid gap-16 pt-10 mt-6 lg:gap-x-5 lg:gap-y-12">
          {filteredPosts.length === 0 ? (
            <div className="flex flex-col col-span-full p-8 md:p-16 bg-secondary">
              <H3 as="p" variant="secondary" className="max-w-lg">
                {t('blog-no-results')}
              </H3>
            </div>
          ) : (
            posts.map(post => (
              <div
                key={post.slug}
                className="col-span-full p-8 md:p-16 bg-secondary"
              >
                <ArticleCard {...post} />
              </div>
            ))
          )}
        </Grid>
        {hasMorePosts ? (
          <div className="flex justify-center my-16 w-full">
            <Button
              variant="secondary"
              onClick={() => setIndexToShow(i => i + PAGE_SIZE)}
            >
              <span>{t('blog-load-more')}</span>{' '}
              <PlusIcon width="20px" height="20px" />
            </Button>
          </div>
        ) : null}
      </Section>
    </>
  )
}
